/**
 * Telegraph geometry — a PURE map from the wire `TelegraphShape` to the 2D
 * outline the client draws on the ground, in local ground coordinates
 * (x east, z south; origin = the shape's `x`/`z`; heading convention
 * `atan2(dx, dz)`, so facing 0 points +z — exactly the server's).
 *
 * Fairness contract (VFX-018 D-2; combat.md — danger is legible before it
 * lands): the area this module traces is, to polygon resolution, the set of
 * points the server's `pointInZone` resolves as hit for the SAME payload. The
 * shape's `zone` field is that hit-test's input, derived from the wire fields
 * with the server's own defaults (`DEFAULT_CONE_ARC_DEG`,
 * `DEFAULT_RING_INNER_FRACTION`, `length ?? radius`) and the server's own
 * half-widths (`LINE_HALF_WIDTH_M`, `CROSS_ARM_HALF_WIDTH_M`) — imported from
 * `@great-cloud/shared/client`, never restated here, so the two cannot drift.
 * The unit tests assert polygon membership against `pointInZone` on a grid.
 *
 * Degrade rule (VFX-018 §3, mandatory): a directional kind (cone / line /
 * cross) that arrives without `facing` degrades to a CIRCLE and reports it in
 * `degraded` — never a throw, never a silent mis-draw. The fallback radius is
 * the LARGER of `radius` and `length`, so the fallback over-warns rather than
 * under-warns (a telegraph that hides danger is the defect this module exists
 * to fix).
 *
 * No Three.js here (the builder in `telegraphVisual.ts` turns this into meshes)
 * so it runs under `node --test` with no GPU (the AD-120 harness pattern).
 */

import {
  CROSS_ARM_HALF_WIDTH_M,
  DEFAULT_CONE_ARC_DEG,
  DEFAULT_RING_INNER_FRACTION,
  LINE_HALF_WIDTH_M,
  type TelegraphShape,
  type TelegraphZone,
} from '@great-cloud/shared/client';

/** A point in local ground coordinates (metres; x east, z south). */
export interface Vec2 {
  x: number;
  z: number;
}

/** The analytic outline the polygon traces — what the THREE builder switches on. */
export type TelegraphOutline =
  | { kind: 'circle'; radius: number }
  /** A cone: sector of `radius` (the reach) centred on `facing`, half-angle `halfAngle`. */
  | { kind: 'sector'; radius: number; facing: number; halfAngle: number }
  /** A line: rectangle from the origin along `facing` for `length`, half-width `halfWidth`. */
  | { kind: 'rect'; facing: number; length: number; halfWidth: number }
  /** A ring: annulus between `innerRadius` (the safe hole) and `outerRadius`. */
  | { kind: 'annulus'; innerRadius: number; outerRadius: number }
  /** A cross: two perpendicular bands of `halfWidth` at `facing`/`facing+π/2`, clipped to `radius`. */
  | { kind: 'cross'; facing: number; radius: number; halfWidth: number };

/** How the wind-up fill grows across the window (Bible §14.1: outline → solid). */
export type TelegraphFillMode =
  /** Uniform scale from the origin — the shipped circle behaviour, and right for anything rooted at the source. */
  | 'scale'
  /** The filled band grows from the inner (safe) edge out to the outer edge — a ring's hole must never appear to close. */
  | 'radial';

export interface TelegraphGeometry {
  /** The wire kind the description was derived from. */
  source: TelegraphShape['kind'];
  outline: TelegraphOutline;
  /** Closed polygon tracing the danger area's outer boundary (local ground coordinates, not closed by repetition). */
  polygon: Vec2[];
  /** Closed polygons tracing SAFE interiors cut out of `polygon` (the ring's hole); empty for every other kind. */
  holes: Vec2[][];
  fill: TelegraphFillMode;
  /** The server hit-test input this drawing is the footprint of: `pointInZone(zone, x, z)` is the fill's membership. */
  zone: TelegraphZone;
  /** Present when a directional kind arrived without `facing` and degraded to the circle fallback. */
  degraded?: { from: TelegraphShape['kind']; reason: string };
}

/** Segments around a full circle — matches the shipped `RingGeometry`/`CircleGeometry` resolution. */
export const TELEGRAPH_SEGMENTS = 48;

const DEG2RAD = Math.PI / 180;

/** A unit heading vector for `facing` (the server's `(sin f, cos f)`). */
export function headingVector(facing: number): Vec2 {
  return { x: Math.sin(facing), z: Math.cos(facing) };
}

function finiteOr(value: number | undefined, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

/** Points of a full circle of `radius`, `segments` around, starting at heading 0. */
export function circlePolygon(radius: number, segments = TELEGRAPH_SEGMENTS): Vec2[] {
  const points: Vec2[] = [];
  for (let i = 0; i < segments; i += 1) {
    const heading = (i / segments) * Math.PI * 2;
    points.push({ x: Math.sin(heading) * radius, z: Math.cos(heading) * radius });
  }
  return points;
}

/** Points along an arc of `radius` from heading `from` to `to` inclusive, `count` ≥ 2 samples. */
function arcPoints(radius: number, from: number, to: number, count: number): Vec2[] {
  const n = Math.max(2, count);
  const points: Vec2[] = [];
  for (let i = 0; i < n; i += 1) {
    const heading = from + ((to - from) * i) / (n - 1);
    points.push({ x: Math.sin(heading) * radius, z: Math.cos(heading) * radius });
  }
  return points;
}

/** Sample count for an arc spanning `span` radians at the full-circle resolution. */
function arcSamples(span: number, segments: number): number {
  return Math.max(4, Math.ceil((segments * span) / (Math.PI * 2)) + 1);
}

/**
 * The polygon of a cross: four arms of half-width `w` clipped by the circle of
 * `radius`, traced as ONE closed outline — arm-end arc, inner corner, next arm.
 * Degenerate (`w ≥ radius`, the disc is entirely inside the bands) ⇒ a circle.
 */
export function crossPolygon(
  facing: number,
  radius: number,
  halfWidth: number,
  segments = TELEGRAPH_SEGMENTS,
): Vec2[] {
  if (halfWidth >= radius) return circlePolygon(radius, segments);
  const alpha = Math.asin(halfWidth / radius); // half the angular span of an arm's end arc
  const cornerDist = halfWidth * Math.SQRT2; // the inner corner sits w from both arm axes
  const points: Vec2[] = [];
  for (let k = 0; k < 4; k += 1) {
    const arm = facing + (k * Math.PI) / 2;
    points.push(...arcPoints(radius, arm - alpha, arm + alpha, arcSamples(2 * alpha, segments)));
    const corner = arm + Math.PI / 4;
    points.push({ x: Math.sin(corner) * cornerDist, z: Math.cos(corner) * cornerDist });
  }
  return points;
}

/** The polygon of a sector (cone): apex at the origin, arc of `radius` across `facing ± halfAngle`. */
export function sectorPolygon(
  facing: number,
  radius: number,
  halfAngle: number,
  segments = TELEGRAPH_SEGMENTS,
): Vec2[] {
  if (halfAngle >= Math.PI) return circlePolygon(radius, segments);
  return [
    { x: 0, z: 0 },
    ...arcPoints(
      radius,
      facing - halfAngle,
      facing + halfAngle,
      arcSamples(2 * halfAngle, segments),
    ),
  ];
}

/** The polygon of a line: rectangle from the origin along `facing` for `length`, half-width `halfWidth`. */
export function rectPolygon(facing: number, length: number, halfWidth: number): Vec2[] {
  const u = headingVector(facing);
  // The perpendicular on the +heading side (the direction at facing + π/2).
  const v = { x: Math.cos(facing), z: -Math.sin(facing) };
  return [
    { x: -v.x * halfWidth, z: -v.z * halfWidth },
    { x: u.x * length - v.x * halfWidth, z: u.z * length - v.z * halfWidth },
    { x: u.x * length + v.x * halfWidth, z: u.z * length + v.z * halfWidth },
    { x: v.x * halfWidth, z: v.z * halfWidth },
  ];
}

/** Twice the signed area (shoelace) — sign gives the winding. */
export function signedArea2(poly: Vec2[]): number {
  let a = 0;
  for (let i = 0; i < poly.length; i += 1) {
    const p = poly[i]!;
    const q = poly[(i + 1) % poly.length]!;
    a += p.x * q.z - q.x * p.z;
  }
  return a;
}

/**
 * Inset a simple polygon by `d` metres (each edge moves inward by `d`; corners
 * are mitred, with the miter length clamped to 4·d so a sharp apex cannot
 * spike). Used for the rim band: outline minus its inset.
 */
export function insetPolygon(poly: Vec2[], d: number): Vec2[] {
  const n = poly.length;
  if (n < 3 || d <= 0) return poly.map((p) => ({ ...p }));
  const ccw = signedArea2(poly) > 0;
  const out: Vec2[] = [];
  for (let i = 0; i < n; i += 1) {
    const prev = poly[(i + n - 1) % n]!;
    const cur = poly[i]!;
    const next = poly[(i + 1) % n]!;
    const n1 = inwardNormal(prev, cur, ccw);
    const n2 = inwardNormal(cur, next, ccw);
    let mx = n1.x + n2.x;
    let mz = n1.z + n2.z;
    const ml = Math.hypot(mx, mz);
    if (ml < 1e-9) {
      // A 180° reversal — fall back to the first edge's normal.
      mx = n1.x;
      mz = n1.z;
    } else {
      mx /= ml;
      mz /= ml;
    }
    const cosHalf = Math.max(mx * n1.x + mz * n1.z, 0.25); // miter ≤ 4·d
    const len = d / cosHalf;
    out.push({ x: cur.x + mx * len, z: cur.z + mz * len });
  }
  return out;
}

function inwardNormal(a: Vec2, b: Vec2, ccw: boolean): Vec2 {
  const ex = b.x - a.x;
  const ez = b.z - a.z;
  const l = Math.hypot(ex, ez) || 1;
  // Left normal of the edge is inward for a CCW polygon; right normal for CW.
  return ccw ? { x: -ez / l, z: ex / l } : { x: ez / l, z: -ex / l };
}

/** Even-odd point-in-polygon (ray cast along +x). Boundary points are unspecified. */
export function pointInPolygon(poly: Vec2[], p: Vec2): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i, i += 1) {
    const a = poly[i]!;
    const b = poly[j]!;
    const crosses = a.z > p.z !== b.z > p.z;
    if (crosses && p.x < ((b.x - a.x) * (p.z - a.z)) / (b.z - a.z) + a.x) inside = !inside;
  }
  return inside;
}

/** Membership of a point in a described geometry (outer polygon minus its holes). */
export function pointInGeometry(geometry: TelegraphGeometry, p: Vec2): boolean {
  if (!pointInPolygon(geometry.polygon, p)) return false;
  return !geometry.holes.some((hole) => pointInPolygon(hole, p));
}

/**
 * Describe a wire telegraph shape as the outline the client draws. Pure; never
 * throws on a well-typed shape; degrades a directional kind without `facing`.
 */
export function describeTelegraph(
  shape: TelegraphShape,
  segments = TELEGRAPH_SEGMENTS,
): TelegraphGeometry {
  const radius = Math.max(0, finiteOr(shape.radius, 0));
  const length = Math.max(0, finiteOr(shape.length, radius));
  const zone: TelegraphZone = {
    shape: shape.kind,
    x: 0,
    z: 0,
    radius,
    facing: finiteOr(shape.facing, 0),
    arc: finiteOr(shape.arc, DEFAULT_CONE_ARC_DEG * DEG2RAD),
    length,
    innerRadius: Math.min(
      radius,
      Math.max(0, finiteOr(shape.innerRadius, radius * DEFAULT_RING_INNER_FRACTION)),
    ),
    damage: 0,
    school: 'physical',
  };

  const circle = (r: number, degraded?: TelegraphGeometry['degraded']): TelegraphGeometry => ({
    source: shape.kind,
    outline: { kind: 'circle', radius: r },
    polygon: circlePolygon(r, segments),
    holes: [],
    fill: 'scale',
    zone: degraded ? { ...zone, shape: 'circle', radius: r } : zone,
    ...(degraded ? { degraded } : {}),
  });

  const directional = shape.kind === 'cone' || shape.kind === 'line' || shape.kind === 'cross';
  if (directional && typeof shape.facing !== 'number') {
    return circle(Math.max(radius, length), {
      from: shape.kind,
      reason: `directional telegraph '${shape.kind}' arrived without facing — drawn as the omnidirectional fallback (VFX-018 §3)`,
    });
  }

  switch (shape.kind) {
    case 'circle':
    case 'point':
      return circle(radius);
    case 'cone':
      return {
        source: 'cone',
        outline: { kind: 'sector', radius: zone.length, facing: zone.facing, halfAngle: zone.arc },
        polygon: sectorPolygon(zone.facing, zone.length, zone.arc, segments),
        holes: [],
        fill: 'scale',
        zone,
      };
    case 'line':
      return {
        source: 'line',
        outline: {
          kind: 'rect',
          facing: zone.facing,
          length: zone.length,
          halfWidth: LINE_HALF_WIDTH_M,
        },
        polygon: rectPolygon(zone.facing, zone.length, LINE_HALF_WIDTH_M),
        holes: [],
        fill: 'scale',
        zone,
      };
    case 'ring':
      return {
        source: 'ring',
        outline: { kind: 'annulus', innerRadius: zone.innerRadius, outerRadius: radius },
        polygon: circlePolygon(radius, segments),
        holes: zone.innerRadius > 0 ? [circlePolygon(zone.innerRadius, segments)] : [],
        fill: 'radial',
        zone,
      };
    case 'cross':
      return {
        source: 'cross',
        outline: { kind: 'cross', facing: zone.facing, radius, halfWidth: CROSS_ARM_HALF_WIDTH_M },
        polygon: crossPolygon(zone.facing, radius, CROSS_ARM_HALF_WIDTH_M, segments),
        holes: [],
        fill: 'scale',
        zone,
      };
    default:
      return circle(radius);
  }
}
